/**
 * txnHelpers.js — Phase 3
 *
 * Small pure helpers shared by the Transactions redesign. Kept together
 * so /components/transactions/ has a single import surface for derived
 * data (provenance / confidence / avatar tint / day grouping).
 */

// Deterministic hash → one of 6 muted tint tokens. Same account id
// always maps to the same tint so a user's Chase account (say)
// looks visually identical everywhere on the page.
const BADGE_TINTS = [
  { bg: 'hsl(120 15% 92%)', fg: 'hsl(150 25% 30%)' }, // evergreen soft
  { bg: 'hsl(35 40% 92%)',  fg: 'hsl(35 45% 35%)'  }, // gold soft
  { bg: 'hsl(210 20% 92%)', fg: 'hsl(210 30% 35%)' }, // slate
  { bg: 'hsl(280 15% 92%)', fg: 'hsl(280 25% 40%)' }, // plum
  { bg: 'hsl(190 25% 92%)', fg: 'hsl(190 40% 30%)' }, // teal
  { bg: 'hsl(15 35% 92%)',  fg: 'hsl(15 45% 40%)'  }, // clay
];

function hashString(s) {
  let h = 0;
  const str = String(s || '');
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) - h) + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export function accountTint(accountId) {
  if (!accountId) return { bg: 'hsl(220 10% 94%)', fg: 'hsl(220 15% 45%)' };
  return BADGE_TINTS[hashString(accountId) % BADGE_TINTS.length];
}

export function accountInitials(name) {
  if (!name) return '·';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Provenance derivation. The FE renders a small icon + tooltip; the
// caller resolves the actual icon component so this file stays free of
// react-imports and is easy to unit-test.
export function provenanceOf(txn) {
  const s = (txn?.source || '').toLowerCase();
  const cs = (txn?.category_source || '').toLowerCase();
  if (s === 'statement' || s === 'import') return { key: 'statement', label: 'Imported from a statement' };
  if (s === 'receipt' || cs === 'ocr')     return { key: 'receipt',   label: 'Scanned from a receipt' };
  if (txn?.recurring_id)                    return { key: 'recurring', label: 'Linked to a recurring commitment' };
  return { key: 'manual', label: 'Added manually' };
}

// Confidence derivation for categorisation. Three buckets so the pill
// visual stays legible. Manual/user-set is always High; rule/merchant
// mappings are Medium; AI/auto guesses are Low. Anything unknown falls
// back to Medium so we never call out 'Low' without evidence.
export function confidenceOf(txn) {
  const cs = (txn?.category_source || '').toLowerCase();
  if (!cs) return { key: 'medium', label: 'Medium confidence' };
  if (cs === 'manual' || cs === 'user') return { key: 'high', label: 'You set this' };
  if (cs === 'rule' || cs === 'merchant' || cs === 'recurring') return { key: 'medium', label: 'Rule / merchant match' };
  if (cs === 'ai' || cs === 'auto' || cs === 'llm') return { key: 'low', label: 'Auto-guess — review if wrong' };
  return { key: 'medium', label: 'Medium confidence' };
}

export function isLowConfidence(txn) {
  return confidenceOf(txn).key === 'low';
}

// Group transactions by local date (YYYY-MM-DD). Returns [{key, label,
// items, income, expense, investment, refund}]. Newest-first ordering.
export function groupByDay(txns, amountFn) {
  // Phase 84.22.5 — optional `amountFn(txn) → number` lets callers convert
  // native transaction amounts to the user's base currency before summing.
  // Falls back to `Math.abs(t.amount)` for callers that don't care about
  // FX (single-currency dashboards, admin views, etc).
  const getAmount = typeof amountFn === 'function' ? amountFn : (t) => Math.abs(Number(t.amount)) || 0;
  const groups = new Map();
  for (const t of txns || []) {
    const k = (t.txn_date || '').slice(0, 10) || 'unknown';
    if (!groups.has(k)) groups.set(k, { key: k, items: [], income: 0, expense: 0, investment: 0, refund: 0 });
    const g = groups.get(k);
    g.items.push(t);
    const mag = getAmount(t);
    if (t.type === 'income')     g.income     += mag;
    else if (t.type === 'expense')    g.expense    += mag;
    else if (t.type === 'investment') g.investment += mag;
    else if (t.type === 'refund')     g.refund     += mag;
  }
  return Array.from(groups.values()).sort((a, b) => b.key.localeCompare(a.key));
}

// 'Wed, 15 May' — localised, short.
export function dayLabel(iso) {
  if (!iso || iso === 'unknown') return 'No date';
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' });
  } catch { return iso; }
}
